// @ts-nocheck
import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Calendar, 
  Euro, 
  CheckCircle2, 
  Clock, 
  Target, 
  Key, 
  Globe, 
  Sparkles,
  TrendingUp,
  FileText,
  Users,
  Mail,
  Phone,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Download,
  Loader2,
  Printer
} from 'lucide-react';
import jsPDF from 'jspdf';

export const CafeMaxContractPage: React.FC = () => {
  const documentRef = useRef<HTMLDivElement>(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const contractData = {
    signatureDate: '16 Novembre 2025',
    period: 'Décembre 2025 - Décembre 2026',
    pack: 'STRATOS Premium',
    monthlyPrice: 1700,
    monthlyPriceTTC: 2040,
    duration: 12,
    contact: {
      name: 'Frédéric Vardon',
      title: 'Chef & Propriétaire',
      email: 'bistrot@cafemax.fr',
      phone: '+33 1 47 05 57 66'
    }
  };

  const months = [
    'Déc. 2025', 'Janv. 2026', 'Fév. 2026', 'Mars 2026', 
    'Avr. 2026', 'Mai 2026', 'Juin 2026', 'Juil. 2026',
    'Août 2026', 'Sept. 2026', 'Oct. 2026', 'Nov. 2026'
  ];

  const advantages = [
    { icon: Euro, text: 'Tarif public pack Premium : 2 000 € HT / mois' },
    { icon: CheckCircle2, text: 'Tarif Café Max : 1 700 € HT / mois' },
    { icon: FileText, text: 'Audit initial complet offert (4 000 € HT)' },
    { icon: Users, text: '2 h de consulting stratégique / mois' },
    { icon: Target, text: 'Accès au dashboard STRATOS' }
  ];

  const month1Actions = [
    'Réécriture des titles et meta descriptions',
    'Création fichiers robots.txt et sitemap.xml',
    'Mise en place des données structurées (Restaurant, Menu, Horaires, Avis...)',
    'Optimisation des images (ALT + compression)',
    'Amélioration des performances (cache, compression, sécurité)',
    'Préparation multilingue (FR / EN)'
  ];

  const month2Actions = [
    'Création de 4 pages piliers : Histoire du lieu, Page Chef, Terrasse / ambiance, Déjeuner d\'affaires / privatisation',
    'Mise en place du blog',
    'Rédaction de 2 premiers articles',
    'Lancement d\'une campagne de backlinks (presse culinaire, hôtels, influenceurs)'
  ];

  const month3Actions = [
    'Optimisation complète Google Business Profile',
    'Ajout massif de photos professionnelles',
    'Posts réguliers (3 / semaine)',
    'Système de collecte d\'avis (QR code + lien dédié)',
    'Ligne éditoriale Instagram + publications régulières',
    'Premiers tests Facebook Ads géolocalisées'
  ];

  const accessCritical = [
    { name: 'Accès administrateur WordPress', icon: Globe },
    { name: 'Google Analytics 4', icon: Target },
    { name: 'Google Search Console', icon: Target },
    { name: 'Google Business Profile', icon: Globe }
  ];

  const accessRecommended = [
    { name: 'FTP / hébergement', icon: Key },
    { name: 'Instagram et Facebook Pages', icon: Globe },
    { name: 'Outil de réservation (Zenchef, TheFork…)', icon: Target },
    { name: 'Photos HD / logos / contenus', icon: FileText }
  ];

  const accessOptional = [
    { name: 'Plateforme emailing (Mailchimp, Brevo…)', icon: Mail },
    { name: 'Bing Webmaster Tools', icon: Globe }
  ];

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (isGeneratingPDF) return;
    
    setIsGeneratingPDF(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - 2 * margin;
      let yPosition = margin;

      // Couleurs (toujours en RGB 0-255)
      const goldColor = [233, 188, 102]; // #E9BC66
      const blackColor = [0, 0, 0];
      const whiteColor = [255, 255, 255];
      const lightGray = [245, 245, 245];

      // Utilitaires
      const formatAmount = (amount: number) => {
        return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      };

      const addNewPage = () => {
        pdf.addPage();
        yPosition = margin;
      };

      const checkPageBreak = (heightNeeded: number) => {
        if (yPosition + heightNeeded > pageHeight - margin - 10) {
          addNewPage();
          return true;
        }
        return false;
      };

      const addSectionTitle = (title: string) => {
        checkPageBreak(15);
        pdf.setFillColor(...goldColor);
        pdf.rect(margin, yPosition, contentWidth, 10, 'F');
        pdf.setTextColor(...whiteColor);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text(title, margin + 3, yPosition + 7);
        yPosition += 15;
        pdf.setTextColor(...blackColor);
      };

      // ========== PAGE 1: COUVERTURE ==========
      // Fond blanc
      pdf.setFillColor(...whiteColor);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');

      // Header doré
      pdf.setFillColor(...goldColor);
      pdf.rect(0, 0, pageWidth, 40, 'F');
      pdf.setTextColor(...whiteColor);
      pdf.setFontSize(28);
      pdf.setFont('helvetica', 'bold');
      pdf.text('STRATOS', pageWidth / 2, 18, { align: 'center' });
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Digital Strategy', pageWidth / 2, 28, { align: 'center' });

      // Titre
      yPosition = 60;
      pdf.setTextColor(...blackColor);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Contrat STRATOS 12 Mois', pageWidth / 2, yPosition, { align: 'center' });
      
      yPosition += 10;
      pdf.setFontSize(20);
      pdf.text('Café Max', pageWidth / 2, yPosition, { align: 'center' });

      yPosition += 15;
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text('Document de lancement officiel', pageWidth / 2, yPosition, { align: 'center' });

      // Box informations - FOND BLANC avec bordure
      yPosition += 20;
      pdf.setDrawColor(...goldColor);
      pdf.setLineWidth(0.5);
      pdf.setFillColor(...whiteColor);
      pdf.roundedRect(margin, yPosition, contentWidth, 55, 3, 3, 'FD');
      
      yPosition += 8;
      pdf.setTextColor(...blackColor);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('INFORMATIONS PRINCIPALES', margin + 5, yPosition);
      
      yPosition += 8;
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Date de signature : ' + contractData.signatureDate, margin + 5, yPosition);
      yPosition += 6;
      pdf.text('Période : ' + contractData.period, margin + 5, yPosition);
      yPosition += 6;
      pdf.text('Pack : ' + contractData.pack, margin + 5, yPosition);
      yPosition += 6;
      pdf.text('Tarif : ' + formatAmount(contractData.monthlyPrice) + ' € HT / mois (remise 15%)', margin + 5, yPosition);
      yPosition += 6;
      pdf.text('Contact : ' + contractData.contact.name, margin + 5, yPosition);
      yPosition += 6;
      pdf.text('Email : ' + contractData.contact.email, margin + 5, yPosition);
      yPosition += 6;
      pdf.text('Téléphone : ' + contractData.contact.phone, margin + 5, yPosition);

      // Footer
      pdf.setTextColor(150, 150, 150);
      pdf.setFontSize(9);
      const dateStr = new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
      pdf.text(dateStr, pageWidth / 2, pageHeight - 15, { align: 'center' });
      pdf.text('Document confidentiel - STRATOS Digital Strategy', pageWidth / 2, pageHeight - 10, { align: 'center' });

      // ========== PAGE 2: ÉCHÉANCIER ==========
      addNewPage();
      addSectionTitle('1. ÉCHÉANCIER DE PAIEMENT');

      // Tableau - FOND BLANC avec bordures noires
      const tableTop = yPosition;
      const colWidths = [35, 35, 30, 30, 30];
      const rowHeight = 8;
      
      // En-tête - fond gris TRÈS clair
      pdf.setFillColor(250, 250, 250);
      pdf.setDrawColor(...blackColor);
      pdf.rect(margin, tableTop, contentWidth, rowHeight, 'FD');
      
      pdf.setTextColor(...blackColor);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      
      let xPos = margin + 2;
      pdf.text('Mois', xPos, tableTop + 5.5);
      xPos += colWidths[0];
      pdf.text('Date', xPos, tableTop + 5.5);
      xPos += colWidths[1];
      pdf.text('Montant HT', xPos, tableTop + 5.5);
      xPos += colWidths[2];
      pdf.text('Montant TTC', xPos, tableTop + 5.5);
      xPos += colWidths[3];
      pdf.text('Statut', xPos, tableTop + 5.5);

      yPosition = tableTop + rowHeight;
      pdf.setFont('helvetica', 'normal');

      // Lignes - FOND BLANC
      months.forEach((month, index) => {
        checkPageBreak(rowHeight);
        
        // Alternance très légère pour lisibilité
        if (index % 2 === 0) {
          pdf.setFillColor(255, 255, 255);
        } else {
          pdf.setFillColor(252, 252, 252);
        }
        pdf.rect(margin, yPosition, contentWidth, rowHeight, 'F');
        
        // Bordures
        pdf.setDrawColor(230, 230, 230);
        pdf.line(margin, yPosition, pageWidth - margin, yPosition);
        
        xPos = margin + 2;
        pdf.setTextColor(...blackColor);
        pdf.text(month, xPos, yPosition + 5.5);
        xPos += colWidths[0];
        pdf.text('1er ' + month.split(' ')[0], xPos, yPosition + 5.5);
        xPos += colWidths[1];
        pdf.text(formatAmount(contractData.monthlyPrice) + ' €', xPos, yPosition + 5.5);
        xPos += colWidths[2];
        pdf.text(formatAmount(contractData.monthlyPriceTTC) + ' €', xPos, yPosition + 5.5);
        xPos += colWidths[3];
        pdf.text('À venir', xPos, yPosition + 5.5);
        
        yPosition += rowHeight;
      });

      // Total - fond or très léger
      pdf.setFillColor(254, 250, 240);
      pdf.rect(margin, yPosition, contentWidth, rowHeight, 'FD');
      pdf.setFont('helvetica', 'bold');
      xPos = margin + 2;
      pdf.text('TOTAL ANNUEL', xPos, yPosition + 5.5);
      xPos += colWidths[0] + colWidths[1];
      pdf.text(formatAmount(contractData.monthlyPrice * 12) + ' € HT', xPos, yPosition + 5.5);
      xPos += colWidths[2];
      pdf.text(formatAmount(contractData.monthlyPriceTTC * 12) + ' € TTC', xPos, yPosition + 5.5);
      
      yPosition += rowHeight + 8;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(100, 100, 100);
      pdf.text('Mode de paiement : Virement bancaire mensuel', margin, yPosition);
      yPosition += 5;
      pdf.text('Référence : STRATOS-CAFEMAX-[MOIS]-[ANNÉE]', margin, yPosition);

      // ========== PAGE 3: AVANTAGES ==========
      addNewPage();
      addSectionTitle('2. AVANTAGES ENGAGEMENT 12 MOIS');

      pdf.setTextColor(...blackColor);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      
      advantages.forEach((advantage, index) => {
        checkPageBreak(7);
        pdf.text('•  ' + advantage.text, margin + 3, yPosition);
        yPosition += 7;
      });

      // ========== PAGE 4: PLAN D'ACTION ==========
      yPosition += 10;
      addSectionTitle('3. PLAN D\'ACTION - 3 PREMIERS MOIS');

      pdf.setFontSize(9);
      pdf.setTextColor(80, 80, 80);
      pdf.text('Objectif : Poser les fondations techniques, renforcer la visibilité et engager une progression', margin, yPosition);
      yPosition += 5;
      pdf.text('régulière du trafic et des réservations.', margin, yPosition);
      yPosition += 10;

      // Mois 1 - Box blanche avec bordure
      checkPageBreak(50);
      pdf.setDrawColor(...goldColor);
      pdf.setLineWidth(0.5);
      pdf.setFillColor(...whiteColor);
      pdf.roundedRect(margin, yPosition, contentWidth, 45, 2, 2, 'FD');
      
      yPosition += 6;
      pdf.setTextColor(...blackColor);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('MOIS 1 - Corrections techniques et SEO de base', margin + 3, yPosition);
      
      yPosition += 6;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Objectif : Assainir et optimiser la base technique du site.', margin + 3, yPosition);
      
      yPosition += 6;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Actions principales :', margin + 3, yPosition);
      yPosition += 5;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      
      month1Actions.forEach((action) => {
        checkPageBreak(5);
        pdf.text('•  ' + action, margin + 5, yPosition);
        yPosition += 4;
      });

      yPosition += 8;

      // Mois 2
      checkPageBreak(40);
      pdf.setDrawColor(...goldColor);
      pdf.setFillColor(...whiteColor);
      pdf.roundedRect(margin, yPosition, contentWidth, 38, 2, 2, 'FD');
      
      yPosition += 6;
      pdf.setTextColor(...blackColor);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('MOIS 2 - Contenus et notoriété', margin + 3, yPosition);
      
      yPosition += 6;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Objectif : Renforcer la qualité éditoriale et la pertinence pour les recherches locales.', margin + 3, yPosition);
      
      yPosition += 6;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Actions principales :', margin + 3, yPosition);
      yPosition += 5;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      
      month2Actions.forEach((action) => {
        checkPageBreak(5);
        pdf.text('•  ' + action, margin + 5, yPosition);
        yPosition += 4;
      });

      yPosition += 8;

      // Mois 3
      checkPageBreak(45);
      pdf.setDrawColor(...goldColor);
      pdf.setFillColor(...whiteColor);
      pdf.roundedRect(margin, yPosition, contentWidth, 42, 2, 2, 'FD');
      
      yPosition += 6;
      pdf.setTextColor(...blackColor);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('MOIS 3 - Visibilité locale & réseaux sociaux', margin + 3, yPosition);
      
      yPosition += 6;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Objectif : Dynamiser la présence dans le Pack Local Google et la communication sociale.', margin + 3, yPosition);
      
      yPosition += 6;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Actions principales :', margin + 3, yPosition);
      yPosition += 5;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      
      month3Actions.forEach((action) => {
        checkPageBreak(5);
        pdf.text('•  ' + action, margin + 5, yPosition);
        yPosition += 4;
      });

      yPosition += 10;

      // Indicateurs - Tableau simple
      checkPageBreak(40);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...blackColor);
      pdf.text('INDICATEURS SUIVIS (objectifs, non garanties)', margin, yPosition);
      yPosition += 8;

      const indicators = [
        { name: 'Score technique', initial: '~45/100', target: '70-75/100' },
        { name: 'Trafic organique mensuel', initial: '10-20 visites', target: '40-80 visites' },
        { name: 'Mots-clés', initial: 'Faible base', target: 'Progression notable' },
        { name: 'Avis Google', initial: '~500', target: '+20 à +50' },
        { name: 'Instagram', initial: '~2 000', target: 'Croissance régulière' },
      ];

      // En-tête tableau
      pdf.setFillColor(250, 250, 250);
      pdf.rect(margin, yPosition, contentWidth, 7, 'F');
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Indicateur', margin + 2, yPosition + 5);
      pdf.text('Situation initiale', margin + 65, yPosition + 5);
      pdf.text('Objectif à 3 mois', margin + 120, yPosition + 5);
      yPosition += 7;

      pdf.setFont('helvetica', 'normal');
      indicators.forEach((ind) => {
        checkPageBreak(7);
        pdf.setFillColor(255, 255, 255);
        pdf.rect(margin, yPosition, contentWidth, 7, 'F');
        pdf.setDrawColor(230, 230, 230);
        pdf.line(margin, yPosition, pageWidth - margin, yPosition);
        
        pdf.setTextColor(...blackColor);
        pdf.text(ind.name, margin + 2, yPosition + 5);
        pdf.text(ind.initial, margin + 65, yPosition + 5);
        pdf.setTextColor(...goldColor);
        pdf.setFont('helvetica', 'bold');
        pdf.text(ind.target, margin + 120, yPosition + 5);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...blackColor);
        yPosition += 7;
      });

      // ========== PAGE 5: ACCÈS ==========
      addNewPage();
      addSectionTitle('4. ACCÈS À FOURNIR');

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(220, 38, 38);
      pdf.text('SEMAINE 1 - Prioritaire', margin, yPosition);
      yPosition += 6;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...blackColor);
      accessCritical.forEach((access) => {
        checkPageBreak(5);
        pdf.text('•  ' + access.name, margin + 3, yPosition);
        yPosition += 5;
      });

      yPosition += 6;
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(234, 179, 8);
      pdf.text('SEMAINE 2 - Recommandé', margin, yPosition);
      yPosition += 6;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...blackColor);
      accessRecommended.forEach((access) => {
        checkPageBreak(5);
        pdf.text('•  ' + access.name, margin + 3, yPosition);
        yPosition += 5;
      });

      yPosition += 6;
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(100, 100, 100);
      pdf.text('SEMAINE 3 - Optionnel', margin, yPosition);
      yPosition += 6;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...blackColor);
      accessOptional.forEach((access) => {
        checkPageBreak(5);
        pdf.text('•  ' + access.name, margin + 3, yPosition);
        yPosition += 5;
      });

      yPosition += 8;
      pdf.setFontSize(8);
      pdf.setTextColor(59, 130, 246);
      pdf.text('Sécurité : Comptes dédiés STRATOS recommandés (contact@stratoshospitality.com).', margin, yPosition);

      // ========== PAGE 6: REFONTE ==========
      yPosition += 15;
      addSectionTitle('5. REFONTE DU SITE WEB');

      pdf.setFontSize(9);
      pdf.setTextColor(...blackColor);
      pdf.text('Le site actuel limite la visibilité : performance moyenne, structure datée, SEO technique', margin, yPosition);
      yPosition += 5;
      pdf.text('incomplet, exploitation limitée du storytelling.', margin, yPosition);
      yPosition += 10;

      // Option A - Box blanche
      checkPageBreak(70);
      pdf.setDrawColor(...goldColor);
      pdf.setLineWidth(0.5);
      pdf.setFillColor(...whiteColor);
      pdf.roundedRect(margin, yPosition, contentWidth, 62, 2, 2, 'FD');
      
      yPosition += 6;
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...goldColor);
      pdf.text('OPTION A - Refonte complète (recommandée)', margin + 3, yPosition);
      
      yPosition += 7;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...blackColor);
      pdf.text('Budget spécial Café Max : 5 000 € HT', margin + 3, yPosition);
      yPosition += 5;
      pdf.setFontSize(8);
      pdf.setTextColor(100, 100, 100);
      pdf.text('(au lieu de 8 500 € HT - tarif public STRATOS)', margin + 3, yPosition);
      yPosition += 6;
      pdf.setTextColor(...blackColor);
      pdf.text('Délai estimé : 6 à 8 semaines', margin + 3, yPosition);
      
      yPosition += 6;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Inclut :', margin + 3, yPosition);
      yPosition += 5;
      pdf.setFont('helvetica', 'normal');
      
      const refontIncludes = [
        'Nouveau design moderne et responsive',
        'Développement complet WordPress',
        'SEO technique intégré dès la conception',
        'Pages clés réorganisées et optimisées',
        'Performance mobile renforcée',
        'Préparation multilingue (FR / EN)'
      ];
      
      refontIncludes.forEach((item) => {
        pdf.text('•  ' + item, margin + 5, yPosition);
        yPosition += 4;
      });

      // ========== PAGE 7: CONTACTS ==========
      yPosition += 15;
      addSectionTitle('6. CONTACTS STRATOS');

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...blackColor);
      pdf.text('Adrien Cerdan', margin, yPosition);
      yPosition += 5;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Directeur', margin, yPosition);
      yPosition += 5;
      pdf.text('Email : contact@stratoshospitality.com', margin, yPosition);
      yPosition += 5;
      pdf.text('Téléphone : +33 6 98 76 54 32', margin, yPosition);
      
      yPosition += 10;
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Support client', margin, yPosition);
      yPosition += 5;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Email : contact@stratoshospitality.com', margin, yPosition);
      yPosition += 5;
      pdf.setFontSize(8);
      pdf.setTextColor(100, 100, 100);

      // Sauvegarder
      const fileName = 'STRATOS_Contrat_CafeMax_' + contractData.signatureDate.replace(/\s+/g, '_') + '.pdf';
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <div className="space-y-8 pb-12">
      {/* Boutons d'action - Masqués à l'impression */}
      <div className="flex items-center justify-end gap-3 print:hidden">
        <Button
          onClick={handlePrint}
          variant="outline"
          className="border-stratos-gold text-stratos-gold hover:bg-stratos-gold/10"
        >
          <Printer className="w-4 h-4 mr-2" />
          Imprimer
        </Button>
        <Button
          onClick={handleDownloadPDF}
          disabled={isGeneratingPDF}
          className="bg-stratos-gold hover:bg-stratos-gold-dark text-gray-900"
        >
          {isGeneratingPDF ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Génération...
            </>
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              Télécharger PDF
            </>
          )}
        </Button>
      </div>

      {/* Contenu à exporter */}
      <div ref={documentRef} className="print:bg-white print:p-0">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-4 print:break-inside-avoid"
      >
        <div className="flex items-center gap-3">
          <Sparkles className="w-8 h-8 text-stratos-gold print:w-6 print:h-6" />
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 via-stratos-gold to-gray-900 bg-clip-text text-transparent print:text-gray-900 print:bg-none print:bg-clip-border">
            Café Max — Contrat STRATOS 12 Mois
          </h1>
        </div>
        <p className="text-lg text-gray-600">
          Document de lancement officiel
        </p>
      </motion.div>

      {/* Informations principales */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="print:break-inside-avoid"
      >
        <Card className="border-stratos-gold/20 shadow-lg print:shadow-none print:border print:border-gray-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-stratos-gold" />
              Informations principales
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-stratos-gold mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Date de signature</p>
                    <p className="font-semibold">{contractData.signatureDate}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-stratos-gold mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Période du contrat</p>
                    <p className="font-semibold">{contractData.period}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Badge variant="default" className="bg-stratos-gold text-gray-900">
                    {contractData.pack}
                  </Badge>
                  <div>
                    <p className="text-sm text-gray-500">Pack choisi</p>
                    <p className="font-semibold">{contractData.monthlyPrice} € HT / mois (remise 15 %)</p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-stratos-gold mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Contact principal</p>
                    <p className="font-semibold">{contractData.contact.name}</p>
                    <p className="text-sm text-gray-600">{contractData.contact.title}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-stratos-gold mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-semibold">{contractData.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-stratos-gold mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Téléphone</p>
                    <p className="font-semibold">{contractData.contact.phone}</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Échéancier de paiement */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="print:break-inside-avoid"
      >
        <Card className="print:shadow-none print:border print:border-gray-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Euro className="w-5 h-5 text-stratos-gold" />
              1. Échéancier de paiement
            </CardTitle>
            <CardDescription>Vue d'ensemble sur 12 mois</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left p-3 font-semibold text-gray-900">Mois</th>
                    <th className="text-left p-3 font-semibold text-gray-900">Date d'échéance</th>
                    <th className="text-right p-3 font-semibold text-gray-900">Montant HT</th>
                    <th className="text-right p-3 font-semibold text-gray-900">Montant TTC</th>
                    <th className="text-center p-3 font-semibold text-gray-900">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((month, index) => (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + index * 0.02 }}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      <td className="p-3 font-medium text-gray-900">{month}</td>
                      <td className="p-3 text-gray-600">1 {month.split(' ')[0]}</td>
                      <td className="p-3 text-right font-semibold text-gray-900">{contractData.monthlyPrice.toLocaleString('fr-FR')} €</td>
                      <td className="p-3 text-right font-semibold text-gray-900">{contractData.monthlyPriceTTC.toLocaleString('fr-FR')} €</td>
                      <td className="p-3 text-center">
                        <Badge variant="outline" className="text-gray-600">À venir</Badge>
                      </td>
                    </motion.tr>
                  ))}
                  <tr className="bg-stratos-gold/10 border-t-2 border-stratos-gold font-bold">
                    <td colSpan={2} className="p-3 text-gray-900">Total annuel</td>
                    <td className="p-3 text-right text-gray-900">{(contractData.monthlyPrice * 12).toLocaleString('fr-FR')} € HT</td>
                    <td className="p-3 text-right text-gray-900">{(contractData.monthlyPriceTTC * 12).toLocaleString('fr-FR')} € TTC</td>
                    <td className="p-3"></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-6 space-y-2 text-sm text-gray-600">
              <p><strong>Mode de paiement :</strong> Virement bancaire mensuel</p>
              <p><strong>Référence :</strong> STRATOS-CAFEMAX-[MOIS]-[ANNÉE]</p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Avantages engagement */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="print:break-inside-avoid"
      >
        <Card className="bg-gradient-to-br from-stratos-gold/10 to-stratos-gold/5 border-stratos-gold/20 print:bg-white print:border print:border-gray-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-stratos-gold" />
              Avantages engagement 12 mois
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {advantages.map((advantage, index) => {
                const Icon = advantage.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className="flex items-start gap-3 p-3 rounded-lg bg-white/50"
                  >
                    <Icon className="w-5 h-5 text-stratos-gold mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-700">{advantage.text}</p>
                  </motion.div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Plan d'action 3 mois */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="print:break-inside-avoid"
      >
        <Card className="print:shadow-none print:border print:border-gray-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-stratos-gold" />
              2. Plan d'action — 3 premiers mois
            </CardTitle>
            <CardDescription>
              Objectif : Poser les fondations techniques, renforcer la visibilité et engager une progression régulière du trafic et des réservations.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Mois 1 */}
            <div className="border-l-4 border-stratos-gold pl-4 space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant="default" className="bg-stratos-gold text-gray-900">
                  Mois 1
                </Badge>
                <h3 className="font-semibold text-lg">Corrections techniques et SEO de base</h3>
              </div>
              <p className="text-sm text-gray-600">
                <strong>Objectif :</strong> Assainir et optimiser la base technique du site.
              </p>
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-700">Actions principales :</p>
                <ul className="space-y-1.5">
                  {month1Actions.map((action, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-stratos-gold mt-0.5 flex-shrink-0" />
                      <span>{action}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Résultat visé :</strong> Score technique : 45/100 → 65–70/100 — Base stable pour la croissance organique
                </p>
              </div>
            </div>

            {/* Mois 2 */}
            <div className="border-l-4 border-stratos-gold pl-4 space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant="default" className="bg-stratos-gold text-gray-900">
                  Mois 2
                </Badge>
                <h3 className="font-semibold text-lg">Contenus et notoriété</h3>
              </div>
              <p className="text-sm text-gray-600">
                <strong>Objectif :</strong> Renforcer la qualité éditoriale et la pertinence pour les recherches locales.
              </p>
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-700">Actions principales :</p>
                <ul className="space-y-1.5">
                  {month2Actions.map((action, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-stratos-gold mt-0.5 flex-shrink-0" />
                      <span>{action}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Résultat visé :</strong> Trafic organique multiplié par 2 à 4 selon saison — Domaine plus fiable, premiers signaux de notoriété
                </p>
              </div>
            </div>

            {/* Mois 3 */}
            <div className="border-l-4 border-stratos-gold pl-4 space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant="default" className="bg-stratos-gold text-gray-900">
                  Mois 3
                </Badge>
                <h3 className="font-semibold text-lg">Visibilité locale & réseaux sociaux</h3>
              </div>
              <p className="text-sm text-gray-600">
                <strong>Objectif :</strong> Dynamiser la présence dans le Pack Local Google et structurer la communication sociale.
              </p>
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-700">Actions principales :</p>
                <ul className="space-y-1.5">
                  {month3Actions.map((action, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-stratos-gold mt-0.5 flex-shrink-0" />
                      <span>{action}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Résultat visé :</strong> Augmentation progressive des avis — Apparitions renforcées dans le Pack Local — Croissance régulière Instagram — Plus de cohérence entre site, Google et social
                </p>
              </div>
            </div>

            {/* Indicateurs */}
            <div className="mt-6">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-stratos-gold" />
                Indicateurs suivis (objectifs, non garanties)
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left p-2 font-semibold text-gray-900">Indicateur</th>
                      <th className="text-left p-2 font-semibold text-gray-900">Situation initiale</th>
                      <th className="text-left p-2 font-semibold text-gray-900">Objectif à 3 mois</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="p-2 font-medium">Score technique</td>
                      <td className="p-2 text-gray-600">~45/100</td>
                      <td className="p-2 text-stratos-gold font-semibold">70–75/100</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-2 font-medium">Trafic organique mensuel</td>
                      <td className="p-2 text-gray-600">10–20 visites</td>
                      <td className="p-2 text-stratos-gold font-semibold">40–80 visites</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-2 font-medium">Mots-clés</td>
                      <td className="p-2 text-gray-600">Faible base</td>
                      <td className="p-2 text-stratos-gold font-semibold">Progression notable sur les requêtes cœur</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-2 font-medium">Avis Google</td>
                      <td className="p-2 text-gray-600">~500</td>
                      <td className="p-2 text-stratos-gold font-semibold">+20 à +50</td>
                    </tr>
                    <tr>
                      <td className="p-2 font-medium">Instagram</td>
                      <td className="p-2 text-gray-600">~2 000</td>
                      <td className="p-2 text-stratos-gold font-semibold">Croissance régulière</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Accès à fournir */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="print:break-inside-avoid"
      >
        <Card className="print:shadow-none print:border print:border-gray-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-stratos-gold" />
              3. Accès à fournir
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Semaine 1 - Prioritaire */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="danger" className="bg-red-100 text-red-800">
                  Semaine 1 — Prioritaire
                </Badge>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                {accessCritical.map((access, index) => {
                  const Icon = access.icon;
                  return (
                    <div key={index} className="flex items-center gap-3 p-3 border border-red-200 rounded-lg bg-red-50/50">
                      <Icon className="w-5 h-5 text-red-600 flex-shrink-0" />
                      <p className="text-sm font-medium text-gray-900">{access.name}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Semaine 2 - Recommandé */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="warning" className="bg-yellow-100 text-yellow-800">
                  Semaine 2 — Recommandé
                </Badge>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                {accessRecommended.map((access, index) => {
                  const Icon = access.icon;
                  return (
                    <div key={index} className="flex items-center gap-3 p-3 border border-yellow-200 rounded-lg bg-yellow-50/50">
                      <Icon className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                      <p className="text-sm font-medium text-gray-900">{access.name}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Semaine 3 - Optionnel */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="outline" className="border-gray-300 text-gray-600">
                  Semaine 3 — Optionnel
                </Badge>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                {accessOptional.map((access, index) => {
                  const Icon = access.icon;
                  return (
                    <div key={index} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50/50">
                      <Icon className="w-5 h-5 text-gray-600 flex-shrink-0" />
                      <p className="text-sm font-medium text-gray-900">{access.name}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-blue-900 mb-1">Sécurité</p>
                  <p className="text-sm text-blue-800">
                    Comptes dédiés STRATOS recommandés (contact@stratoshospitality.com).
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Refonte du site web */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="print:break-inside-avoid"
      >
        <Card className="border-stratos-gold/30 bg-gradient-to-br from-stratos-gold/5 to-transparent print:bg-white print:border print:border-gray-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-stratos-gold" />
              4. Refonte du site web
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="font-semibold mb-2">Constat</h3>
              <p className="text-sm text-gray-700">
                Le site actuel limite la visibilité : performance moyenne, structure datée, SEO technique incomplet, exploitation limitée du storytelling.
              </p>
            </div>

            {/* Option A */}
            <div className="border-l-4 border-stratos-gold pl-4 space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant="default" className="bg-stratos-gold text-gray-900">
                  Option A – Refonte complète (recommandée)
                </Badge>
              </div>
              <div className="space-y-2">
                <p className="text-sm">
                  <strong className="text-stratos-gold">Budget spécial Café Max :</strong> 5 000 € HT
                  <span className="text-gray-500 text-xs ml-2">(au lieu de 8 500 € HT – tarif public STRATOS)</span>
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Délai estimé :</strong> 6 à 8 semaines
                </p>
                <div className="mt-3">
                  <p className="text-sm font-semibold mb-2">Inclut :</p>
                  <ul className="space-y-1.5">
                    {[
                      'Nouveau design moderne et responsive',
                      'Développement complet WordPress',
                      'SEO technique intégré dès la conception',
                      'Pages clés réorganisées et optimisées',
                      'Performance mobile renforcée',
                      'Préparation multilingue (FR / EN)',
                      'Formulaire de réservation optimisé'
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle className="w-4 h-4 text-stratos-gold mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-stratos-gold/10 p-3 rounded-lg mt-3">
                  <p className="text-sm font-semibold text-gray-900 mb-1">Objectif :</p>
                  <p className="text-sm text-gray-700">
                    Trafic organique multiplié par 2 à 3 sur 6–12 mois — Amélioration sensible du taux de conversion — Image en ligne alignée avec le niveau du restaurant
                  </p>
                </div>
              </div>
            </div>

            {/* Option B */}
            <div className="border-l-4 border-gray-300 pl-4 space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant="outline">Option B – Amélioration progressive</Badge>
              </div>
              <p className="text-sm text-gray-700">
                Si la refonte n'est pas immédiate : Corrections techniques et SEO (M1–M2) — Nouvelle version développée ensuite
              </p>
              <p className="text-sm">
                <strong>Budget constant :</strong> 5 000 € HT
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Prochaines étapes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="print:break-inside-avoid"
      >
        <Card className="bg-gradient-to-r from-gray-900 to-gray-800 text-white border-stratos-gold/30 print:bg-white print:text-gray-900 print:border print:border-gray-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white print:text-gray-900">
              <ArrowRight className="w-5 h-5 text-stratos-gold" />
              5. Prochaines étapes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3 list-decimal list-inside print:text-gray-900">
              <li className="text-gray-200 print:text-gray-900">Validation du document et du périmètre.</li>
              <li className="text-gray-200 print:text-gray-900">Transmission des accès essentiels.</li>
              <li className="text-gray-200 print:text-gray-900">Réunion de lancement (1 h).</li>
              <li className="text-gray-200 print:text-gray-900">Démarrage du Mois 1.</li>
            </ol>
          </CardContent>
        </Card>
      </motion.div>

      {/* Contacts */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
        className="print:break-inside-avoid"
      >
        <Card className="print:shadow-none print:border print:border-gray-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-stratos-gold" />
              Contacts STRATOS
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div>
                  <p className="font-semibold text-gray-900">Adrien Cerdan</p>
                  <p className="text-sm text-gray-600">Directeur</p>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-stratos-gold" />
                    <a href="mailto:contact@stratoshospitality.com" className="text-gray-700 hover:text-stratos-gold transition-colors">
                      contact@stratoshospitality.com
                    </a>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-stratos-gold" />
                    <a href="tel:+33698765432" className="text-gray-700 hover:text-stratos-gold transition-colors">
                      +33 6 98 76 54 32
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-stratos-gold" />
                <div>
                  <p className="font-semibold text-gray-900">Support client</p>
                  <a href="mailto:contact@stratoshospitality.com" className="text-sm text-gray-700 hover:text-stratos-gold transition-colors">
                    contact@stratoshospitality.com
                  </a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
      </div>
    </div>
  );
};
